import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Table, Tag } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { AuthenticationState } from "../../interfaces/users";
import { api } from "../../config";
import { SearchFilter } from "../Shared/Components/Filters/SearchFilter";
import {
  ModulePermission,
  userHasPermission,
} from "../../utils/helpers/userHelpers";

const CURITIBA_IBGE_ID = 4106902;

export const Neighborhoods = () => {
  const { token, user } = useSelector((state: AuthenticationState) => state);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File>();
  const [uploading, setUploading] = useState(false);
  const {
    data: municipalities = [],
    isFetching: isFetchingMunicipalities,
  } = useQuery("neighborhoods-municipalities", () =>
    api(token).get("/municipalities", { includeInactive: true })
  );
  const curitibaMunicipality = useMemo(
    () =>
      municipalities.find(
        (municipality: any) => Number(municipality.ibgeId) === CURITIBA_IBGE_ID
      ),
    [municipalities]
  );
  const {
    data: neighborhoods = [],
    isFetching,
    refetch,
  } = useQuery(
    ["neighborhoods", curitibaMunicipality?.id],
    () =>
      api(token).get("/neighborhoods", {
        includeInactive: true,
        municipalityId: curitibaMunicipality?.id,
      }),
    {
      enabled: !!curitibaMunicipality?.id,
    }
  );
  const [tableData, setTableData] = useState<any[]>(neighborhoods);
  const userCanManageRegions = userHasPermission(user, {
    region_management: ModulePermission.ADMIN,
  });

  useEffect(() => {
    setTableData(neighborhoods);
  }, [neighborhoods]);

  const handleUpload = async () => {
    if (!file) {
      return toast.error("Selecione uma planilha xlsx para importar");
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      await api(token).post("/neighborhoods/import-curitiba", formData, {
        "Content-Type": "multipart/form-data",
      });

      toast.success("Bairros importados com sucesso!");
      setFile(undefined);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ??
          "Ocorreu um erro ao importar os bairros"
      );
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
    },
    {
      title: (
        <SearchFilter
          placeholder="Bairro"
          data={neighborhoods}
          setData={setTableData}
          field="name"
        />
      ),
      dataIndex: "name",
    },
    {
      title: "Código Bairro",
      dataIndex: "externalId",
    },
    {
      title: "Município",
      dataIndex: "municipality",
      render: (municipality: any) => municipality?.name ?? "-",
    },
    {
      title: "Status",
      dataIndex: "active",
      render: (active: boolean) => (
        <Tag color={active ? "green" : "default"}>
          {active ? "Ativo" : "Inativo"}
        </Tag>
      ),
    },
  ];

  return (
    <div className="mt-4">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="mb-1">Bairros de Curitiba</h2>
          <p className="m-0">
            Importe manualmente uma planilha xlsx com as colunas
            {" "}
            <b>codigo_bairro</b>
            {" "}
            e
            {" "}
            <b>nome_bairro</b>
            .
          </p>
        </div>
        {userCanManageRegions ? (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              onChange={(event) => setFile(event.target.files?.[0])}
            />
            <Button
              type="primary"
              icon={<UploadOutlined />}
              disabled={!curitibaMunicipality}
              loading={uploading}
              onClick={handleUpload}
            >
              Importar xlsx
            </Button>
          </div>
        ) : null}
      </div>

      <Table
        rowKey="id"
        bordered
        loading={isFetching || isFetchingMunicipalities}
        dataSource={tableData}
        columns={columns}
        pagination={{ position: ["bottomCenter"] }}
      />
    </div>
  );
};
